import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, AlertTriangle, Key, Fingerprint, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';

const DeveloperSecurityGate = ({ children, requiredLevel = 'developer' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [biometricCode, setBiometricCode] = useState('');
  const [authAttempts, setAuthAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [stage, setStage] = useState(1); // 1: primary auth, 2: biometric

  // 🔐 קודי אבטחה מתקדמים (רק בקוד!)
  const SECURITY_CODES = {
    developer: {
      primary: 'SHA-DEV-ADVANCED-2025',
      biometric: 'BEN-BIOMETRIC-ULTRA-SECURE'
    },
    admin: {
      primary: 'SHA-ADMIN-SECURE-2025',
      biometric: 'ADMIN-BIO-VERIFICATION'
    }
  };

  const MAX_AUTH_ATTEMPTS = 3;
  const LOCKOUT_DURATION = 30 * 60 * 1000; // 30 דקות

  // בדיקת נעילה
  const isLockedOut = () => {
    if (!lockoutUntil) return false;
    return Date.now() < lockoutUntil;
  };

  // בדיקת זיכרון מקומי לאימות שמור
  useEffect(() => {
    const savedAuth = localStorage.getItem(`dev_auth_${requiredLevel}`);
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);
      if (authData.expiry > Date.now()) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem(`dev_auth_${requiredLevel}`);
      }
    }
  }, [requiredLevel]);

  // אימות שלב ראשון
  const handlePrimaryAuth = () => {
    if (isLockedOut()) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / (1000 * 60));
      toast.error(`מערכת נעולה. נסה שוב בעוד ${remainingTime} דקות`);
      return;
    }

    const codes = SECURITY_CODES[requiredLevel];
    if (authCode === codes.primary) {
      setStage(2);
      setAuthCode('');
      toast.success('🔓 שלב ראשון אושר - נדרש אימות ביומטרי');
    } else {
      const newAttempts = authAttempts + 1;
      setAuthAttempts(newAttempts);
      
      if (newAttempts >= MAX_AUTH_ATTEMPTS) {
        setLockoutUntil(Date.now() + LOCKOUT_DURATION);
        toast.error('🔒 יותר מדי ניסיונות שגויים. המערכת נעולה ל-30 דקות');
      } else {
        toast.error(`קוד שגוי. נותרו ${MAX_AUTH_ATTEMPTS - newAttempts} ניסיונות`);
      }
      setAuthCode('');
    }
  };

  // אימות ביומטרי (שלב שני)
  const handleBiometricAuth = () => {
    const codes = SECURITY_CODES[requiredLevel];
    if (biometricCode === codes.biometric) {
      setIsAuthenticated(true);
      setAuthAttempts(0);
      setLockoutUntil(null);
      
      // שמירת אימות למשך 4 שעות
      const authData = {
        authenticated: true,
        level: requiredLevel,
        expiry: Date.now() + (4 * 60 * 60 * 1000)
      };
      localStorage.setItem(`dev_auth_${requiredLevel}`, JSON.stringify(authData));
      
      toast.success('🎉 אימות מתקדם הושלם בהצלחה!');
    } else {
      const newAttempts = authAttempts + 1;
      setAuthAttempts(newAttempts);
      
      if (newAttempts >= MAX_AUTH_ATTEMPTS) {
        setLockoutUntil(Date.now() + LOCKOUT_DURATION);
        setStage(1);
        toast.error('🔒 אימות ביומטרי נכשל. חזרה לשלב ראשון');
      } else {
        toast.error(`קוד ביומטרי שגוי. נותרו ${MAX_AUTH_ATTEMPTS - newAttempts} ניסיונות`);
      }
      setBiometricCode('');
    }
  };

  // יציאה מהמערכת
  const handleLogout = () => {
    setIsAuthenticated(false);
    setStage(1);
    setAuthCode('');
    setBiometricCode('');
    localStorage.removeItem(`dev_auth_${requiredLevel}`);
    toast.success('יצאת מהמערכת המאובטחת');
  };

  // אם מאומת - הצג את התוכן
  if (isAuthenticated) {
    return (
      <div className="relative">
        {/* כפתור יציאה */}
        <div className="absolute top-4 left-4 z-50">
          <button
            onClick={handleLogout}
            className="flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
          >
            <Lock className="w-4 h-4 mr-2" />
            יציאה מאובטחת
          </button>
        </div>
        
        {/* תוכן מוגן */}
        <div className="pt-16">
          {children}
        </div>
      </div>
    );
  }

  // מסך אימות
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center"
    >
      <motion.div 
        initial={{ scale: 0.8, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-gray-900 border-2 border-red-500/30 rounded-2xl p-8 w-full max-w-md shadow-2xl backdrop-blur-sm"
      >
        {/* כותרת אבטחה */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            {stage === 1 ? (
              <Shield className="w-16 h-16 text-red-500" />
            ) : (
              <Fingerprint className="w-16 h-16 text-blue-500" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {stage === 1 ? 'אבטחה מתקדמת' : 'אימות ביומטרי'}
          </h2>
          <p className="text-gray-400">
            {stage === 1 
              ? `גישה מוגבלת למפתחי ${requiredLevel === 'developer' ? 'מערכת' : 'מנהלים'} בלבד` 
              : 'נדרש אימות ביומטרי נוסף'
            }
          </p>
          <div className="flex items-center justify-center mt-2 text-sm text-yellow-400">
            <Key className="w-4 h-4 mr-1" />
            <span>רמת אבטחה: {requiredLevel.toUpperCase()}</span>
          </div>
        </div>

        {/* אזהרת אבטחה */}
        {isLockedOut() && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
            <div className="flex items-center text-red-400">
              <AlertTriangle className="w-5 h-5 mr-2" />
              <span>מערכת נעולה זמנית</span>
            </div>
          </div>
        )}

        {/* שלב 1: אימות ראשוני */}
        {stage === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Shield className="w-4 h-4 inline mr-2" />
                קוד אבטחה ראשוני
              </label>
              <input
                type="password"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePrimaryAuth()}
                disabled={isLockedOut()}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 font-mono"
                placeholder="הכנס קוד אבטחה מתקדם..."
              />
            </div>

            <motion.button
              onClick={handlePrimaryAuth}
              disabled={isLockedOut() || !authCode.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {isLockedOut() ? (
                <span className="flex items-center justify-center">
                  <Lock className="w-4 h-4 mr-2" />
                  מערכת נעולה
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  אימות שלב ראשון
                </span>
              )}
            </motion.button>
          </div>
        )}

        {/* שלב 2: אימות ביומטרי */}
        {stage === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Fingerprint className="w-4 h-4 inline mr-2" />
                קוד ביומטרי מתקדם
              </label>
              <input
                type="password"
                value={biometricCode}
                onChange={(e) => setBiometricCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBiometricAuth()}
                className="w-full px-4 py-3 bg-gray-800 border border-blue-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                placeholder="הכנס קוד ביומטרי..."
              />
            </div>

            <motion.button
              onClick={handleBiometricAuth}
              disabled={!biometricCode.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              <span className="flex items-center justify-center">
                <Fingerprint className="w-4 h-4 mr-2" />
                אימות ביומטרי
              </span>
            </motion.button>

            <button
              onClick={() => {
                setStage(1);
                setBiometricCode('');
              }}
              className="w-full text-gray-400 hover:text-white text-sm py-2"
            >
              חזרה לשלב ראשון
            </button>
          </div>
        )}

        {/* מידע על ניסיונות */}
        {authAttempts > 0 && !isLockedOut() && (
          <div className="text-center text-sm text-yellow-400 mt-4">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            נותרו {MAX_AUTH_ATTEMPTS - authAttempts} ניסיונות
          </div>
        )}

        {/* מידע נוסף */}
        <div className="mt-6 text-center text-xs text-gray-500 border-t border-gray-700 pt-4">
          <Eye className="w-3 h-3 inline mr-1" />
          קומפוננטה מוגנת ברמת אבטחה {requiredLevel.toUpperCase()}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DeveloperSecurityGate; 